import { API_BASE_PATH, type ApiError } from '@ytclip/shared'
import type { z } from 'zod'

const BASE_URL = import.meta.env.VITE_API_URL ?? ''

export class ApiRequestError extends Error {
  readonly status: number
  readonly code: string
  readonly details: unknown

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message)
    this.name = 'ApiRequestError'
    this.status = status
    this.code = code
    this.details = details
  }

  get isUnauthenticated(): boolean {
    return this.status === 401
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'
  body?: unknown
  query?: Record<string, string | number | undefined>
  signal?: AbortSignal
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(`${BASE_URL}${API_BASE_PATH}${path}`, window.location.origin)

  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined) url.searchParams.set(key, String(value))
  }

  return url.toString()
}

async function toApiError(response: Response): Promise<ApiRequestError> {
  const fallback = `Request failed with status ${response.status}`

  try {
    const payload = (await response.json()) as ApiError
    return new ApiRequestError(
      response.status,
      payload.error?.code ?? 'INTERNAL',
      payload.error?.message ?? fallback,
      payload.error?.details,
    )
  } catch {
    return new ApiRequestError(response.status, 'INTERNAL', fallback)
  }
}

/**
 * The single way the client reaches the API. `credentials: 'include'` sends the
 * session cookie; nothing else is ever attached, because the browser holds no
 * secrets in V2.
 *
 * Passing a schema validates the response, so a backend contract change surfaces
 * as a clear error instead of an undefined property deep in a component.
 */
export async function apiRequest<TSchema extends z.ZodTypeAny>(
  path: string,
  schema: TSchema,
  options: RequestOptions = {},
): Promise<z.infer<TSchema>> {
  const { method = 'GET', body, query, signal } = options

  const response = await fetch(buildUrl(path, query), {
    method,
    credentials: 'include',
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  })

  if (!response.ok) {
    throw await toApiError(response)
  }

  if (response.status === 204) {
    return schema.parse(undefined)
  }

  return schema.parse(await response.json())
}
